// src/components/LandingPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Mic as MicIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Devices as DevicesIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  FamilyRestroom as FamilyIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
} from '@mui/icons-material';

// Strukturierte Daten für SEO
const structuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "BrainWizzr",
  "applicationCategory": "EducationalApplication",
  "operatingSystem": "Web Browser",
  "applicationSubCategory": "Language Learning",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "127",
    "bestRating": "5",
    "worstRating": "1"
  },
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "EUR",
    "availability": "https://schema.org/InStock"
  },
  "description": "KI-gestützte Sprachlernplattform mit Vokabeltrainer, Spaced Repetition und Konversationstraining",
  "screenshot": "https://www.brainwizzr.app/BrainWizzr-Hero.png",
  "featureList": "Vokabeltrainer, Spaced Repetition, KI-Konversationstraining, Aussprache-Feedback, Grammatiktraining",
  "inLanguage": "de"
};

// Organization Schema für Brand-Erkennung
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "BrainWizzr",
  "url": "https://www.brainwizzr.app",
  "logo": "https://www.brainwizzr.app/BrainWizzr-Logo.svg",
  "description": "BrainWizzr ist eine KI-gestützte Sprachlernplattform für personalisiertes Sprachenlernen.",
  "foundingDate": "2024",
  "areaServed": "DE",
  "sameAs": []
};

// WebSite Schema für Sitelinks
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "BrainWizzr",
  "url": "https://www.brainwizzr.app",
  "description": "KI-gestützte Sprachlernplattform mit Vokabeltrainer und Konversationstraining",
  "inLanguage": "de-DE",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://www.brainwizzr.app/?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
};

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Ist BrainWizzr kostenlos?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ja, BrainWizzr bietet einen kostenlosen Basiszugang. Für erweiterte KI-Features gibt es Premium-Optionen ab 6,67€/Monat (Jahresabo)."
      }
    },
    {
      "@type": "Question",
      "name": "Welche Sprachen kann ich lernen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Mit BrainWizzr kannst du jede Sprache lernen - von Englisch über Spanisch bis hin zu Japanisch. Du kannst sogar eigene Fachthemen erstellen."
      }
    },
    {
      "@type": "Question",
      "name": "Wie funktioniert das KI-Konversationstraining?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Unsere KI analysiert deine gesprochenen Sätze in Echtzeit, prüft Grammatik und Vokabelverwendung und gibt dir sofortiges Feedback - wie ein persönlicher Sprachlehrer."
      }
    },
    {
      "@type": "Question",
      "name": "Funktioniert BrainWizzr auf dem Smartphone?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ja, BrainWizzr ist eine responsive Web-App und funktioniert perfekt auf Desktop, Tablet und Smartphone."
      }
    }
  ]
};

// Feature-Daten - Blau-Töne passend zum Logo
const features = [
  {
    icon: <PsychologyIcon sx={{ fontSize: 48 }} />,
    title: 'KI-Sprachlehrer',
    description: 'Echte Konversationen mit KI, die deine Sätze analysiert und sofortiges Feedback gibt.',
    color: '#1e40af'
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 48 }} />,
    title: 'Spaced Repetition',
    description: 'Wissenschaftlich bewiesene Methode für langfristiges Behalten. Lerne weniger, merke mehr.',
    color: '#2563eb'
  },
  {
    icon: <MicIcon sx={{ fontSize: 48 }} />,
    title: 'Aussprache-Training',
    description: 'Sprich Vokabeln laut aus und erhalte Feedback zu Aussprache und Grammatik.',
    color: '#3b82f6'
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
    title: 'Fortschritts-Tracking',
    description: 'Detaillierte Statistiken zeigen deinen Lernfortschritt und motivieren dich weiterzumachen.',
    color: '#0ea5e9'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 48 }} />,
    title: 'DSGVO-konform',
    description: 'Deine Daten sind sicher. Hosting in Deutschland, volle Transparenz.',
    color: '#0284c7'
  },
  {
    icon: <DevicesIcon sx={{ fontSize: 48 }} />,
    title: 'Überall lernen',
    description: 'Desktop, Tablet, Smartphone - dein Fortschritt synchronisiert sich automatisch.',
    color: '#0369a1'
  }
];

// Zielgruppen
const targetGroups = [
  {
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    title: 'Schüler & Studenten',
    description: 'Perfekt für Vokabeltests, Klausuren und Sprachzertifikate wie TOEFL oder TestDaF.',
    image: '/BrainWizzr-Schueler.jpeg'
  },
  {
    icon: <WorkIcon sx={{ fontSize: 40 }} />,
    title: 'Berufstätige',
    description: 'Business-Englisch, Fachvokabular und flexible Lernzeiten für deinen Karriereschub.',
    image: '/BrainWizzr-Business.jpeg'
  },
  {
    icon: <LanguageIcon sx={{ fontSize: 40 }} />,
    title: 'Deutsch als Fremdsprache',
    description: 'Alltagssprache, Behördengänge, Bewerbungen - wir helfen dir, dich in Deutschland zurechtzufinden.',
    image: '/BrainWizzr-Integration.jpeg'
  },
  {
    icon: <FamilyIcon sx={{ fontSize: 40 }} />,
    title: 'Familien',
    description: 'Gamification und kindgerechte Inhalte motivieren auch die Jüngsten zum Sprachenlernen.',
    image: '/BrainWizzr-Familie.jpeg'
  }
];

// Testimonials
const testimonials = [
  {
    name: 'Anna M.',
    role: 'Studentin, 23',
    avatar: 'A',
    text: 'Endlich eine App, die mich nicht langweilt! Die KI-Gespräche sind unglaublich hilfreich für mein Auslandssemester.',
    rating: 5
  },
  {
    name: 'Markus K.',
    role: 'IT-Manager, 35',
    avatar: 'M',
    text: 'Ich lerne täglich 10 Minuten Business-Englisch auf dem Weg zur Arbeit. Mein Team hat den Unterschied sofort bemerkt.',
    rating: 5
  },
  {
    name: 'Fatima A.',
    role: 'DaF-Lernende, 28',
    avatar: 'F',
    text: 'Die App hat mir geholfen, mich auf mein Vorstellungsgespräch vorzubereiten. Jetzt arbeite ich in meinem Traumjob!',
    rating: 5
  }
];

// FAQ-Daten
const faqs = [
  {
    question: 'Ist BrainWizzr kostenlos?',
    answer: 'Ja! BrainWizzr bietet einen kostenlosen Basiszugang mit allen wichtigen Funktionen. Für erweiterte KI-Features wie das Konversationstraining gibt es Premium-Optionen ab 6,67€/Monat (Jahresabo für 79,99€).'
  },
  {
    question: 'Welche Sprachen kann ich lernen?',
    answer: 'Mit BrainWizzr kannst du jede Sprache lernen - von den gängigen wie Englisch, Spanisch, Französisch bis hin zu Japanisch oder Arabisch. Du kannst sogar eigene Fachthemen erstellen, um z.B. medizinisches Vokabular zu lernen.'
  },
  {
    question: 'Wie funktioniert das KI-Konversationstraining?',
    answer: 'Unsere KI-Sprachlehrerin hört dir in Echtzeit zu, analysiert deine gesprochenen Sätze und gibt dir sofortiges Feedback zu Grammatik, Vokabelverwendung und Aussprache - wie ein persönlicher Sprachlehrer, aber rund um die Uhr verfügbar.'
  },
  {
    question: 'Wie unterscheidet sich BrainWizzr von Duolingo oder Babbel?',
    answer: 'Während andere Apps auf vorgefertigte Lektionen setzen, passt sich BrainWizzr individuell an dich an. Du kannst eigene Vokabeln hinzufügen, echte KI-Gespräche führen und von wissenschaftlich fundierter Spaced Repetition profitieren.'
  },
  {
    question: 'Ist meine Daten sicher?',
    answer: 'Absolut. BrainWizzr ist DSGVO-konform, unsere Server stehen in Deutschland, und wir verschlüsseln alle Daten. Du behältst die volle Kontrolle über deine Informationen.'
  }
];

// Schritte "So funktioniert's"
const howItWorks = [
  {
    step: 1,
    title: 'Sprache wählen',
    description: 'Wähle deine Zielsprache oder erstelle ein eigenes Fachthema.',
    image: '/BrainWizzr-Sprachauswahl.png'
  },
  {
    step: 2,
    title: 'Vokabeln hinzufügen',
    description: 'Füge Vokabeln manuell hinzu, importiere Listen oder nutze KI-Übersetzung.',
    image: '/BrainWizzr-Vokabeleingabe.png'
  },
  {
    step: 3,
    title: 'Smart lernen',
    description: 'Unser Algorithmus zeigt dir Vokabeln genau dann, wenn du sie wiederholen solltest.',
    image: '/BrainWizzr-Vokabeltraining.png'
  },
  {
    step: 4,
    title: 'Sprechen üben',
    description: 'Führe echte Gespräche mit unserer KI und erhalte sofortiges Feedback.',
    image: '/BrainWizzr-Konversationstraining.png'
  }
];

export default function LandingPage() {
  const [expandedFaq, setExpandedFaq] = useState(false);

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  return (
    <>
      <Helmet>
        <title>BrainWizzr - KI-Sprachlernplattform | Vokabeltrainer & Konversationstraining</title>
        <meta
          name="description"
          content="Lerne Sprachen mit KI-Unterstützung. Vokabeltrainer mit Spaced Repetition, echte KI-Gespräche und Aussprache-Feedback. Kostenlos starten - für Schüler, Studenten und Berufstätige."
        />
        <meta name="keywords" content="Vokabeltrainer, Sprachlern-App, KI Sprachlehrer, Deutsch lernen, Englisch lernen, Spanisch lernen, Spaced Repetition, Konversationstraining, Aussprache üben, Grammatiktraining" />
        <link rel="canonical" href="https://www.brainwizzr.app/" />
        {/* Strukturierte Daten für Suchmaschinen */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>

      {/* ==================== HERO SECTION ==================== */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1e40af 100%)',
          color: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(37, 99, 235, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="🚀 Neu: KI-Konversationstraining"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.2)',
                  fontWeight: 500
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mb: 3,
                  background: 'linear-gradient(90deg, #fff 0%, #c7d2fe 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Sprachen lernen mit deinem persönlichen KI-Lehrer
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}
              >
                Vokabeltrainer mit Spaced Repetition, echte Gespräche mit KI und 
                sofortiges Feedback. Lerne effizienter als je zuvor.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/signup"
                  sx={{
                    bgcolor: '#2563eb',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.4)',
                    '&:hover': {
                      bgcolor: '#1d4ed8',
                      boxShadow: '0 6px 20px 0 rgba(37, 99, 235, 0.5)',
                    }
                  }}
                >
                  Kostenlos starten
                </Button>
              </Box>
              <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                {[
                  '✓ Kostenlos starten',
                  '✓ Keine Kreditkarte',
                  '✓ KI-gestützt'
                ].map((item, i) => (
                  <Typography key={i} variant="body2" sx={{ opacity: 0.8 }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Hero Image */}
              <Box
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <img
                  src="/BrainWizzr-Hero.png"
                  alt="BrainWizzr App - Vokabeltrainer mit KI"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ==================== HIGHLIGHT BAR ==================== */}
      <Box sx={{ py: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 3, md: 6 },
              flexWrap: 'wrap'
            }}
          >
            {[
              { label: 'Unbegrenzte Sprachen', icon: '🌍' },
              { label: 'KI-Konversationen', icon: '🤖' },
              { label: 'Spaced Repetition', icon: '🧠' },
              { label: 'Made in Germany', icon: '🇩🇪' }
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">{item.icon}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ==================== FEATURES SECTION ==================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                mb: 2,
                color: '#1e293b'
              }}
            >
              Alles, was du zum Sprachenlernen brauchst
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto', fontWeight: 400 }}
            >
              Wissenschaftlich fundierte Methoden, moderne KI-Technologie und ein 
              Design, das Spaß macht.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 3,
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px -10px rgba(0,0,0,0.1)',
                      borderColor: feature.color
                    }
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 2,
                        bgcolor: `${feature.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: feature.color
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: '#1e293b' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== HOW IT WORKS ==================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                mb: 2,
                color: '#1e293b'
              }}
            >
              In 4 Schritten zum Sprachprofi
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto', fontWeight: 400 }}
            >
              Starte in wenigen Minuten und erlebe, wie effektiv Sprachenlernen sein kann.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {howItWorks.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: '#2563eb',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      mx: 'auto',
                      mb: 3
                    }}
                  >
                    {item.step}
                  </Box>
                  {/* Screenshot */}
                  <Box
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 2,
                      mb: 3,
                      overflow: 'hidden',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1e293b' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== TARGET GROUPS ==================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                mb: 2,
                color: '#1e293b'
              }}
            >
              Für jeden das Richtige
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto', fontWeight: 400 }}
            >
              Egal ob Schüler, Student, Berufstätiger oder Spracheinsteiger - 
              BrainWizzr passt sich deinen Bedürfnissen an.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {targetGroups.map((group, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 12px 24px -10px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  {/* Zielgruppen-Bild */}
                  <Box
                    sx={{
                      width: { xs: '100%', sm: 180 },
                      minHeight: 160,
                      flexShrink: 0,
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={group.image}
                      alt={group.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#2563eb' }}>
                      {group.icon}
                      <Typography variant="h6" sx={{ ml: 1.5, fontWeight: 600, color: '#1e293b' }}>
                        {group.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {group.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== TESTIMONIALS ==================== */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1e40af 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                mb: 2
              }}
            >
              Das sagen unsere Nutzer
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.8, maxWidth: 600, mx: 'auto', fontWeight: 400 }}
            >
              Erfahre, wie andere Lernende von BrainWizzr profitieren.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 4,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                    color: 'white'
                  }}
                >
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIcon key={i} sx={{ color: '#fbbf24', fontSize: 20 }} />
                    ))}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ mb: 3, fontStyle: 'italic', lineHeight: 1.7, color: 'white' }}
                  >
                    "{testimonial.text}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      sx={{
                        bgcolor: '#2563eb',
                        width: 44,
                        height: 44,
                        mr: 2,
                        fontWeight: 600,
                        color: 'white'
                      }}
                    >
                      {testimonial.avatar}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== FAQ SECTION ==================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                mb: 2,
                color: '#1e293b'
              }}
            >
              Häufig gestellte Fragen
            </Typography>
          </Box>

          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expandedFaq === `panel${index}`}
              onChange={handleFaqChange(`panel${index}`)}
              elevation={0}
              sx={{
                mb: 2,
                border: '1px solid #e2e8f0',
                borderRadius: '12px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: '0 0 16px 0'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 3,
                  '& .MuiAccordionSummary-content': {
                    my: 2
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* ==================== FINAL CTA ==================== */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 3
            }}
          >
            Bereit, eine neue Sprache zu meistern?
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 5, opacity: 0.9, fontWeight: 400, maxWidth: 500, mx: 'auto' }}
          >
            Starte jetzt kostenlos und entdecke, wie viel Spaß Sprachenlernen machen kann.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/signup"
            sx={{
              bgcolor: 'white',
              color: '#2563eb',
              px: 5,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 4px 14px 0 rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: '#f8fafc',
                boxShadow: '0 6px 20px 0 rgba(0,0,0,0.25)',
              }
            }}
          >
            Jetzt kostenlos registrieren
          </Button>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            {[
              'Keine Kreditkarte nötig',
              'Sofort loslegen',
              'Jederzeit kündbar'
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon sx={{ fontSize: 20, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
}


